import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import useQuery from 'core/utils/use-query.hook';
import useAuthToken, { DAYS_DELAY, HOURS_DELAY } from 'utils/useAuthToken';
import { login } from 'actions/app.actions';
import { LoginTypes } from 'interfaces';
import { tokenSelector, loginLoadingSelector } from 'reducers/app.reducer';
import { ROUTES } from '../../constants';

interface ILoginFormType extends LoginTypes {
  rememberMe: boolean;
}

type useLoginOutputType = {
  handleLogin: (value: ILoginFormType) => void;
  initialValues: ILoginFormType;
  loading: boolean;
  loginSchema: Yup.ObjectSchema;
  t: (value: string) => string;
};

const initialValues: ILoginFormType = {
  username: '',
  password: '',
  rememberMe: false,
};

const useLoginFormEvents = (): useLoginOutputType => {
  const { t } = useTranslation();
  const token = useSelector(tokenSelector);
  const loading = useSelector(loginLoadingSelector);
  const { setCookie } = useAuthToken();
  const history = useHistory();
  const { returnPath } = useQuery();
  const dispatch = useDispatch();

  const loginSchema = Yup.object().shape({
    username: Yup.string().required(t('core.required')),
    password: Yup.string().required(t('core.required')),
  });

  useEffect(() => {
    if (token) {
      history.push(ROUTES.HOME);
    }
  }, [token, history]);

  /**
   * Remove rememberMe from values before handle login
   * if rememberMe is checked save token during 10 days else save it 1 day
   * @param values
   */
  const handleLogin = (values: ILoginFormType): void => {
    const { rememberMe, ...rest } = values;
    dispatch(
      login.request(rest, (token: string) => {
        setCookie(token, rememberMe ? DAYS_DELAY : HOURS_DELAY);
        const redirectToHomePage = () => history.replace(ROUTES.HOME);
        if (!returnPath) {
          redirectToHomePage();
        } else {
          try {
            const decodedReturnPath = decodeURIComponent(returnPath);

            if (decodedReturnPath.startsWith(ROUTES.BASENAME)) {
              history.replace(
                `${decodedReturnPath.slice(ROUTES.BASENAME.length + 1)}`,
              );
            } else {
              window.location.replace(
                `${window.location.origin}${decodedReturnPath}`,
              );
            }
          } catch (error) {
            console.error(error);
            redirectToHomePage();
          }
        }
      }),
    );
  };

  return {
    handleLogin,
    initialValues,
    loading,
    loginSchema,
    t,
  };
};

export default useLoginFormEvents;
